import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GREETING_LANGUAGES,
  GREETING_LANGUAGE_LABELS,
  type GreetingLanguage
} from "@glowin/schemas";
import { api, ApiError } from "../api.js";
import {
  isDirty,
  toForm,
  toProfile,
  validate,
  type ProfileForm
} from "../profile-model.js";
import { usePageTitle } from "../ui.js";
import {
  AlertIcon,
  CheckIcon,
  ClockIcon,
  GlobeIcon,
  Hint,
  PhoneIcon,
  PinIcon,
  StoreIcon,
  TransferIcon,
  WithIcon
} from "../icons.js";

/**
 * Restaurant details the agent relies on: what it calls the place, where to send people,
 * which languages to speak, and who to hand a caller to when it can't help.
 */
export function InfoPage() {
  usePageTitle("Details");
  const { restaurantId = "" } = useParams();
  const qc = useQueryClient();
  const key = ["profile", restaurantId];

  const loaded = useQuery({
    queryKey: key,
    queryFn: () => api.getProfile(restaurantId),
    // No profile row yet is an expected state for a new location, not a failure.
    retry: false
  });
  const [form, setForm] = useState<ProfileForm | null>(null);
  const [original, setOriginal] = useState<ProfileForm | null>(null);
  const [touched, setTouched] = useState(false);

  // Seed the form once the fetch settles — including the 404 case, which yields a blank
  // form so an operator can create the profile.
  useEffect(() => {
    if (loaded.isLoading || form) return;
    const seeded = toForm(loaded.data);
    setForm(seeded);
    setOriginal(seeded);
  }, [loaded.isLoading, loaded.data, form]);

  const save = useMutation({
    mutationFn: (f: ProfileForm) => api.putProfile(restaurantId, toProfile(f)),
    onSuccess: (saved) => {
      const fresh = toForm(saved);
      setForm(fresh);
      setOriginal(fresh);
      setTouched(false);
      void qc.invalidateQueries({ queryKey: key });
    }
  });

  if (loaded.isLoading || !form || !original) return <p className="muted">Loading details…</p>;

  const errors = validate(form);
  const dirty = isDirty(form, original);
  const hasErrors = Object.keys(errors).length > 0;
  const isNew = !loaded.data;
  const set = (p: Partial<ProfileForm>) => setForm({ ...form, ...p });
  // Only surface field errors once the user has tried to save, so a blank new form
  // isn't covered in red before they've typed anything.
  const errFor = (k: keyof ProfileForm) => (touched ? errors[k] : undefined);

  return (
    <div className="form-page">
      <div className="page-head">
        <h1>
          Restaurant details <Hint>What the phone agent knows about this location.</Hint>
        </h1>
        <div className="page-actions">
          {dirty && <span className="muted">Unsaved changes</span>}
          <button
            className="primary"
            disabled={!dirty || save.isPending || isNew}
            onClick={() => {
              setTouched(true);
              if (!hasErrors) save.mutate(form);
            }}
          >
            <WithIcon icon={<CheckIcon size={16} />}>{save.isPending ? "Saving…" : "Save"}</WithIcon>
          </button>
        </div>
      </div>

      {isNew ? (
        <div className="notice warn">
          <WithIcon icon={<AlertIcon size={16} />}>Not set up yet</WithIcon>
          <Hint>Setup, including your phone number, is done by glowin support. Contact us to get started.</Hint>
        </div>
      ) : null}

      {save.error ? (
        <div className="notice error">
          {save.error instanceof ApiError ? save.error.message : String(save.error)}
        </div>
      ) : null}
      {save.isSuccess && !dirty ? (
        <div className="notice">
          <WithIcon icon={<CheckIcon size={16} />}>Saved</WithIcon>
        </div>
      ) : null}

      <div className="card form-card">
        <label className="field">
          <span>
            <WithIcon icon={<StoreIcon size={15} />}>Restaurant name</WithIcon>
            <Hint>The agent opens with “Thanks for calling {form.name || "…"}”.</Hint>
          </span>
          <input
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            aria-invalid={Boolean(errFor("name"))}
            placeholder="Sichuan Impression"
            autoFocus
          />
          {errFor("name") ? <em className="error">{errFor("name")}</em> : null}
        </label>

        <label className="field">
          <span>
            <WithIcon icon={<PinIcon size={15} />}>Address</WithIcon>
            <Hint>Read out if a caller asks where to collect.</Hint>
          </span>
          <input
            value={form.address}
            onChange={(e) => set({ address: e.target.value })}
            aria-invalid={Boolean(errFor("address"))}
            placeholder="9425 Irvine Center Dr, Irvine, CA"
          />
          {errFor("address") ? <em className="error">{errFor("address")}</em> : null}
        </label>
      </div>

      <div className="card form-card">
        <h2>
          <WithIcon icon={<PhoneIcon size={18} />}>Phone numbers</WithIcon>
        </h2>

        <label className="field">
          <span>
            Number customers call
            <Hint>Assigned by glowin. Calls to this number reach this restaurant.</Hint>
          </span>
          {/* Assigned by glowin, never editable here: this is the dialed-number -> restaurant
              mapping, so a restaurant claiming another's number would divert its calls. */}
          <input
            value={form.phoneE164}
            readOnly
            placeholder="—"
            aria-describedby="inbound-help"
          />
          {/* Don't describe routing for a number that isn't there — that combination read as
              a contradiction: a claim about "this number" with no number shown. */}
          <em className="muted" id="inbound-help">
            {form.phoneE164 ? "Assigned by glowin" : "Not assigned yet"}
          </em>
        </label>

        <label className="field">
          <span>
            <WithIcon icon={<TransferIcon size={15} />}>Staff number for transfers</WithIcon>{" "}
            <span className="muted">(optional)</span>
            <Hint>
              Where the agent sends a caller who needs a person. Leave blank and the agent says it can’t
              transfer instead of pretending it did.
            </Hint>
          </span>
          <input
            value={form.staffPhone}
            onChange={(e) => set({ staffPhone: e.target.value })}
            aria-invalid={Boolean(errFor("staffPhone"))}
            placeholder="+15551234567"
          />
          {errFor("staffPhone") ? <em className="error">{errFor("staffPhone")}</em> : null}
        </label>
      </div>

      <div className="card form-card">
        <h2>
          <WithIcon icon={<GlobeIcon size={18} />}>Language &amp; time</WithIcon>
        </h2>

        <label className="field">
          <span>
            <WithIcon icon={<ClockIcon size={15} />}>Timezone</WithIcon>
            <Hint>Opening hours are read in this zone.</Hint>
          </span>
          <input
            value={form.timezone}
            onChange={(e) => set({ timezone: e.target.value })}
            aria-invalid={Boolean(errFor("timezone"))}
            placeholder="America/Los_Angeles"
          />
          {errFor("timezone") ? <em className="error">{errFor("timezone")}</em> : null}
        </label>

        <label className="field">
          <span>
            Preferred language
            <Hint>The agent greets callers in this language, then follows whichever language the caller speaks.</Hint>
          </span>
          <select
            value={form.preferredLanguage}
            onChange={(e) => set({ preferredLanguage: e.target.value as GreetingLanguage })}
          >
            {GREETING_LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {GREETING_LANGUAGE_LABELS[l]}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
